/**
 * ScrollController - pure logic for the randomly-scrolling viewport.
 *
 * Owns:
 *   - Camera position in tile coordinates
 *   - Scroll timing (when the next scroll happens, current interval)
 *   - Deterministic direction selection (seeded RNG, independent from playfield gen)
 *   - Telegraph state (the brief warning before each scroll)
 *
 * Does NOT know about Phaser, cameras, or rendering. The scene calls update()
 * each frame and reads cameraTileX/cameraTileY + telegraph to update its visuals.
 */

import { makeRng, type RNG } from '../maze/prng';

export type Direction = 'up' | 'down' | 'left' | 'right';

// --- Tuning knobs ---------------------------------------------------------

/** Initial ms between scrolls (start of a run). */
const INTERVAL_START_MS = 1500;

/** Minimum scroll interval (fastest the game gets). */
const INTERVAL_MIN_MS = 400;

/** Elapsed time at which the interval should reach INTERVAL_MIN_MS. */
const ACCEL_DURATION_MS = 60000;

/** Telegraph (arrow) visible duration before each scroll. */
const TELEGRAPH_MS = 200;

// --- Wandering-target tuning ---------------------------------------------
// The scroll picks a random "target" camera position and biases direction
// selection toward it. This makes the random walk cover the playfield more
// completely (uniform random walks in 2D drift very slowly — O(sqrt(N))).
// A new target is picked when the current one is reached, or after a few
// scrolls (whichever comes first), so the camera never gets stuck.

/** How strongly directions toward the target are favored (weight ratio). */
const TARGET_BIAS = 4;
/** Minimum scrolls before forcing a new target. */
const TARGET_MIN_SCROLLS = 10;
/** Maximum extra scrolls beyond the minimum. (Total: MIN..MIN+EXTRA scrolls.) */
const TARGET_EXTRA_SCROLLS = 8;
/**
 * Minimum Manhattan distance between a freshly picked target and the current
 * camera position. Forces meaningful camera travel between targets.
 */
const TARGET_MIN_DIST = 12;
/**
 * When picking a new target, sample this many candidates and choose the one
 * least recently visited (using camera-position visit history). Higher = more
 * deterministic coverage, lower = more random feel. 8 hits a good balance.
 */
const TARGET_CANDIDATES = 8;
/**
 * Multiplier applied to staleness sums (scrolls-since-last-seen) when
 * weighting per-direction choices. Lower = more random feel; higher =
 * more deterministic exploration.
 */
const UNVISITED_BONUS = 0.1;
/**
 * Synthetic staleness assigned to never-seen tiles. Set high enough that
 * unseen tiles dominate over old-stale tiles, so the camera prioritizes
 * filling in the gaps before revisiting known territory.
 */
const UNSEEN_TILE_VALUE = 1000;

/**
 * Weight added to a candidate viewport's score for each uncollected coconut
 * it contains. UNSEEN_TILE_VALUE (1000) dominates early in a run when half
 * the map is fresh; once most tiles have been seen, staleness scores drop
 * into the low hundreds and the coconut bonus becomes the dominant tiebreaker.
 * That's the point: coverage drives early scrolls; coconuts drive late ones.
 *
 * Determinism: the coconut set is fixed at level generation (also seeded).
 * Position-based bias added here uses no new randomness — the same scroll
 * RNG still drives the actual direction choice. So a player who plays an
 * identical sequence of inputs gets an identical sequence of scrolls.
 */
const COCONUT_BONUS = 1500;
/** Stronger pull toward hidden coconuts (★) — easier to miss than visible ones. */
const HIDDEN_COCONUT_BONUS = 2200;

// --------------------------------------------------------------------------

export interface ScrollControllerOptions {
  seed: string;
  /** Playfield width/height in tiles. */
  worldW: number;
  worldH: number;
  /** Viewport size in tiles. */
  viewW: number;
  viewH: number;
  /** Initial camera position (top-left of viewport, tile coords). */
  initialCameraX: number;
  initialCameraY: number;
  /**
   * Multiplier applied to both INTERVAL_START_MS and INTERVAL_MIN_MS.
   * Adventure scaling: lower = faster scroll throughout the level.
   * Default 1 (unaltered).
   */
  intervalMul?: number;
}

export interface ScrollUpdate {
  /** True on the frame a scroll happened. */
  scrolled: boolean;
  /** Direction that just scrolled (null if no scroll this frame). */
  dir: Direction | null;
}

export class ScrollController {
  private rng: RNG;
  private camX: number;
  private camY: number;
  private readonly worldW: number;
  private readonly worldH: number;
  private readonly viewW: number;
  private readonly viewH: number;

  private elapsedMs = 0;
  private nextScrollAtMs: number;
  private interval: number;
  private nextDir: Direction;
  private scrollCount = 0;
  private readonly intervalMul: number;

  /** Camera position the scroll is wandering toward. */
  private targetX: number;
  private targetY: number;
  /** Scrolls remaining before the target must be rerolled. */
  private targetScrollsLeft: number;

  /**
   * For each world tile, the scroll count at which it was last in the
   * viewport. Indexed by y * worldW + x. -1 means never seen. Used by both
   * target selection and per-direction bias.
   *
   * Using "scroll count last seen" rather than a boolean means tiles can
   * become "stale" again over time, preventing the camera from neglecting
   * pockets it briefly visited early on.
   */
  private tileSeenAt: Int32Array;

  /**
   * Per-tile coconut bonus weight. 0 for tiles with no coconut; COCONUT_BONUS
   * or HIDDEN_COCONUT_BONUS for tiles holding an uncollected coconut. Updated
   * by setCoconutPositions() as the player collects them, so the bias shrinks
   * over time and the camera doesn't keep visiting already-cleared zones.
   */
  private coconutWeight: Float32Array;

  constructor(opts: ScrollControllerOptions) {
    // Independent RNG so playfield generation changes don't affect scroll order.
    this.rng = makeRng(`${opts.seed}::scroll`);

    this.camX = opts.initialCameraX;
    this.camY = opts.initialCameraY;
    this.worldW = opts.worldW;
    this.worldH = opts.worldH;
    this.viewW = opts.viewW;
    this.viewH = opts.viewH;
    this.intervalMul = opts.intervalMul ?? 1;

    this.interval = INTERVAL_START_MS * this.intervalMul;
    this.nextScrollAtMs = this.interval;
    // Placeholder; pickNewTarget will override before rollDirection uses them.
    this.targetX = this.camX;
    this.targetY = this.camY;
    this.targetScrollsLeft = 0;
    // Track when each world tile was last in the viewport (-1 = never).
    // Tiles unseen for many scrolls bias the camera to revisit.
    this.tileSeenAt = new Int32Array(this.worldW * this.worldH).fill(-1);
    this.coconutWeight = new Float32Array(this.worldW * this.worldH);
    this.markCurrentViewport();
    this.pickNewTarget();
    this.nextDir = this.rollDirection();
  }

  /**
   * Tell the scroll picker where the uncollected coconuts are. The scene
   * calls this once at startup with all coconuts, then again whenever one is
   * collected (passing only the still-uncollected ones).
   *
   * This is the bias's only source of input — same RNG drives the random
   * picks, so a player playing identical inputs gets identical scrolls.
   */
  setCoconutPositions(
    positions: ReadonlyArray<{ x: number; y: number; invisible: boolean }>
  ): void {
    this.coconutWeight.fill(0);
    for (const c of positions) {
      const i = c.y * this.worldW + c.x;
      this.coconutWeight[i] = c.invisible ? HIDDEN_COCONUT_BONUS : COCONUT_BONUS;
    }
  }

  private markCurrentViewport(): void {
    for (let dy = 0; dy < this.viewH; dy++) {
      for (let dx = 0; dx < this.viewW; dx++) {
        const idx = (this.camY + dy) * this.worldW + (this.camX + dx);
        this.tileSeenAt[idx] = this.scrollCount;
      }
    }
  }

  /**
   * Staleness score for a candidate camera position: sum of (scrollsAgo + 1)
   * for each tile in its viewport. Never-seen tiles contribute a huge bonus
   * so the camera prioritizes finishing exploration before revisiting.
   */
  private stalenessAt(cx: number, cy: number): number {
    let s = 0;
    for (let dy = 0; dy < this.viewH; dy++) {
      for (let dx = 0; dx < this.viewW; dx++) {
        const idx = (cy + dy) * this.worldW + (cx + dx);
        const last = this.tileSeenAt[idx]!;
        if (last < 0) s += UNSEEN_TILE_VALUE;
        else s += this.scrollCount - last;
        s += this.coconutWeight[idx]!;
      }
    }
    return s;
  }

  /**
   * Advance the controller by dt ms. May trigger up to one scroll per call.
   * (If dt is huge and overshoots multiple intervals, we still only fire once
   * to keep camera moves visible — guards against tab-throttling teleports.)
   */
  update(dt: number): ScrollUpdate {
    this.elapsedMs += dt;
    this.interval = this.computeInterval(this.elapsedMs);

    if (this.elapsedMs >= this.nextScrollAtMs) {
      const dir = this.nextDir;
      this.applyScroll(dir);
      this.scrollCount++;
      // Record every world tile newly visible after this scroll.
      this.markCurrentViewport();

      // Schedule next scroll relative to the *intended* moment, not the
      // current elapsedMs — this keeps the cadence honest even if a frame
      // ran long.
      this.nextScrollAtMs += this.interval;

      // If we're still way behind (large dt), snap forward so we don't
      // immediately fire another scroll.
      if (this.elapsedMs > this.nextScrollAtMs) {
        this.nextScrollAtMs = this.elapsedMs + this.interval;
      }

      this.nextDir = this.rollDirection();
      return { scrolled: true, dir };
    }

    return { scrolled: false, dir: null };
  }

  /**
   * If we're within the telegraph window (the last TELEGRAPH_MS before the
   * next scroll), returns the direction that's about to fire and how much
   * of the telegraph has elapsed (0..1).
   */
  get telegraph(): { dir: Direction; progress: number } | null {
    const remaining = this.nextScrollAtMs - this.elapsedMs;
    if (remaining > TELEGRAPH_MS || remaining <= 0) return null;
    const progress = 1 - remaining / TELEGRAPH_MS; // 0 = just appeared, 1 = about to scroll
    return { dir: this.nextDir, progress };
  }

  get cameraTileX(): number {
    return this.camX;
  }

  get cameraTileY(): number {
    return this.camY;
  }

  get scrolls(): number {
    return this.scrollCount;
  }

  get currentIntervalMs(): number {
    return this.interval;
  }

  get elapsed(): number {
    return this.elapsedMs;
  }

  // --- Internals --------------------------------------------------------

  private computeInterval(t: number): number {
    const ratio = Math.min(1, t / ACCEL_DURATION_MS);
    const base =
      INTERVAL_START_MS - ratio * (INTERVAL_START_MS - INTERVAL_MIN_MS);
    return Math.max(INTERVAL_MIN_MS * this.intervalMul, base * this.intervalMul);
  }

  private rollDirection(): Direction {
    // Refresh target if reached or expired.
    const atTarget = this.camX === this.targetX && this.camY === this.targetY;
    if (atTarget || this.targetScrollsLeft <= 0) {
      this.pickNewTarget();
    }
    this.targetScrollsLeft--;

    // Build list of valid (in-bounds) directions and their weights.
    // Weight = (toward-target bonus) × (1 + UNVISITED_BONUS × newTiles)
    // where newTiles is how many world tiles in the resulting viewport
    // haven't been seen yet. A direction that reveals fresh ground AND
    // moves toward the target wins decisively.
    const dirs: Direction[] = [];
    const weights: number[] = [];

    const consider = (
      dir: Direction,
      dx: number,
      dy: number,
      towardTarget: boolean
    ): void => {
      const nx = this.camX + dx;
      const ny = this.camY + dy;
      // Sum staleness along the strip that would become visible by moving.
      let stripStaleness: number;
      if (dx === 1) stripStaleness = this.stalenessColumn(nx + this.viewW - 1, ny, this.viewH);
      else if (dx === -1) stripStaleness = this.stalenessColumn(nx, ny, this.viewH);
      else if (dy === 1) stripStaleness = this.stalenessRow(nx, ny + this.viewH - 1, this.viewW);
      else stripStaleness = this.stalenessRow(nx, ny, this.viewW);

      let w = 1 + UNVISITED_BONUS * stripStaleness;
      if (towardTarget) w *= TARGET_BIAS;
      dirs.push(dir);
      weights.push(w);
    };

    if (this.camX > 0) {
      consider('left', -1, 0, this.targetX < this.camX);
    }
    if (this.camX < this.worldW - this.viewW) {
      consider('right', 1, 0, this.targetX > this.camX);
    }
    if (this.camY > 0) {
      consider('up', 0, -1, this.targetY < this.camY);
    }
    if (this.camY < this.worldH - this.viewH) {
      consider('down', 0, 1, this.targetY > this.camY);
    }

    return dirs[this.weightedIndex(weights)]!;
  }

  /** Sum staleness of a vertical strip of `len` tiles starting at (x, y). */
  private stalenessColumn(x: number, y: number, len: number): number {
    let s = 0;
    for (let i = 0; i < len; i++) {
      const idx = (y + i) * this.worldW + x;
      const last = this.tileSeenAt[idx]!;
      s += last < 0 ? UNSEEN_TILE_VALUE : this.scrollCount - last;
      s += this.coconutWeight[idx]!;
    }
    return s;
  }

  /** Sum staleness of a horizontal strip of `len` tiles starting at (x, y). */
  private stalenessRow(x: number, y: number, len: number): number {
    let s = 0;
    for (let i = 0; i < len; i++) {
      const idx = y * this.worldW + x + i;
      const last = this.tileSeenAt[idx]!;
      s += last < 0 ? UNSEEN_TILE_VALUE : this.scrollCount - last;
      s += this.coconutWeight[idx]!;
    }
    return s;
  }

  private weightedIndex(weights: number[]): number {
    let total = 0;
    for (const w of weights) total += w;
    let r = this.rng.next() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= weights[i]!;
      if (r <= 0) return i;
    }
    return weights.length - 1;
  }

  private pickNewTarget(): void {
    const maxX = this.worldW - this.viewW;
    const maxY = this.worldH - this.viewH;

    // Sample several edge-biased candidates, then pick the one whose viewport
    // would reveal the most still-unseen world tiles. Ties broken by greater
    // distance (so the camera commits to longer journeys). If every candidate
    // would reveal zero new tiles (we've seen everything), fall back to a
    // random edge-biased pick.
    let bestX = -1;
    let bestY = -1;
    let bestScore = -1;

    for (let i = 0; i < TARGET_CANDIDATES; i++) {
      const tx = this.edgeBiased(maxX);
      const ty = this.edgeBiased(maxY);
      const dist = Math.abs(tx - this.camX) + Math.abs(ty - this.camY);
      if (dist < TARGET_MIN_DIST) continue;

      // Score: total staleness of the candidate's viewport, with tiny
      // distance tiebreaker for longer journeys.
      const score = this.stalenessAt(tx, ty) + dist * 0.01;

      if (score > bestScore) {
        bestScore = score;
        bestX = tx;
        bestY = ty;
      }
    }

    if (bestX >= 0) {
      this.targetX = bestX;
      this.targetY = bestY;
    } else {
      this.targetX = this.edgeBiased(maxX);
      this.targetY = this.edgeBiased(maxY);
    }
    this.targetScrollsLeft =
      TARGET_MIN_SCROLLS + this.rng.int(0, TARGET_EXTRA_SCROLLS);
  }

  /**
   * Pick a value in [0, max] biased toward the endpoints. ~65% of picks land
   * in the outer thirds of the range; ~35% land uniformly across the middle.
   */
  private edgeBiased(max: number): number {
    const band = Math.max(2, Math.floor(max / 3));
    const roll = this.rng.next();
    if (roll < 0.325) return this.rng.int(0, band);
    if (roll < 0.65) return this.rng.int(max - band, max);
    return this.rng.int(0, max);
  }

  private applyScroll(dir: Direction): void {
    switch (dir) {
      case 'left':
        this.camX--;
        break;
      case 'right':
        this.camX++;
        break;
      case 'up':
        this.camY--;
        break;
      case 'down':
        this.camY++;
        break;
    }
  }
}
